import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { messages as viewsMessages } from '@databases/api-views/messages';
import { getGroupField, getSubgroupField } from '@databases/grouping';
import { type DatabaseGroup } from '@databases/grouping/database-group';
import { type DatabaseGroupSelectField } from '@databases/grouping/database-group-select-field';
import { selectEntriesWithGrouping } from '@databases/grouping/selectors/select-entries-with-grouping';
import {
  selectSortedGroups,
  selectSortedSubgroups
} from '@databases/grouping/selectors/select-sorted-groups';
import { getImageUrl } from '@databases/media-client/media-client-core';
import { type DefaultRootState } from '@databases/redux';
import {
  DatabaseFieldType,
  type FieldWithContext,
  type RenderContext,
  type SelectOption
} from '@databases/types/database';
import {
  DatabaseCardWidth,
  DatabaseLayoutType,
  type DatabaseViewSettings,
  MIN_CARD_WIDTH,
  shouldShowField
} from '@databases/types/database/view';
import { TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH } from '@databases/utils/constants';
import { getEntryCover } from '@databases/utils/coverImage';
import {
  isEmptyValue,
  renderExportView,
  renderExportViewAsync
} from '@databases/utils/database/field-types';
import { getSelectOptionTag } from '@databases/utils/database/field-types/select';
import {
  getConvertedDatabaseValue,
  getCoverField,
  getDatabaseEntries,
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
  getDatabaseFields,
  getEntryName,
  getFirstFieldId,
  getVisibleFieldsForDatabaseCard,
  isBrokenDatabase
} from '@databases/utils/database/utils';
import { applyDatabaseView } from '@databases/utils/database/view/view';
import { selectBaseUrl } from '@databases/utils/redux/context/selectors';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import { deserializeError, encodeHtml, trimCss } from '@databases/utils/utils';
import type { IntlShape } from 'react-intl-next';

import { getExperimentValue } from '@canvas/client-feature-flags';

// The functions in this file produce Confluence storage format that render the export view of database content.
// This storage format can be used in two separate ways:
// - Sent directly to the convert body API to be converted into storage format (for example in the export dialog)
// - Returned by the static macro endpoint for the Databases macros.
// It is important to note that even though the two situations seem similar, they behave very differently. While the
// convert body API seems to keep all CSS properties, the static macro rendering strips any relatively modern CSS
// properties from the output. This makes it very difficult to apply advanced styling to the output.
// Some properties that don't work in static macro mode:
// - display: flex, display: grid and anything related (such as gap, grid-template, flex-direction etc.)
// - calc()
// - box-sizing
// - overflow-wrap
// - box-shadow
// - aspect-ratio
// - object-fit
// - rgba()

const CLASS_PREFIX = 'nct-databases';
const VALUE_CLASS = `${CLASS_PREFIX}-value`;
const ENTRY_CLASS = `${CLASS_PREFIX}-entry`;
const DATABASE_CLASS = `${CLASS_PREFIX}-database`;

function renderFieldName(context: FieldWithContext): string {
  return context.field.name
    ? `<span style="word-wrap: normal">${encodeHtml(context.field.name)}</span>`
    : '';
}

export function renderDatabaseValue(
  {
    contentId,
    fieldId,
    entryId,
    renderContext,
    selectDatabase = selectDatabaseByContentIdOptional,
    style,
    tag = 'span',
    skipIfEmpty
  }: {
    contentId: string;
    fieldId: string;
    entryId: string;
    renderContext: RenderContext;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
    style?: string;
    tag?: string;
    skipIfEmpty?: boolean;
  },
  state: DefaultRootState
): string {
  const database = selectDatabase(state, { contentId });
  if (!database || isBrokenDatabase(database)) {
    throw database?.error
      ? deserializeError(database.error)
      : new Error('Database is not loaded.');
  }
  const field = getDatabaseFieldRequired(database, fieldId);
  const entry = getDatabaseEntryRequired(database, entryId);
  const value = getConvertedDatabaseValue(database, field, entry, state);

  if (skipIfEmpty && isEmptyValue({ database, field, entry, value }, state)) {
    return '';
  }

  const isValueEmpty = isEmptyValue({ database, field, entry, value }, state);
  const renderedValueHtml = renderExportView(
    { database, field, entry, value },
    state,
    renderContext,
    0,
    contentId
  );
  // Output &nbsp; if value is empty to avoid producing a self-closing div, which might confuse some export view renderers
  return `<${tag} data-is-empty="${isValueEmpty}" class="${VALUE_CLASS} ${VALUE_CLASS}-${encodeHtml(
    value.type
  )}"${style ? ` style="${encodeHtml(style)}"` : ''}>${
    renderedValueHtml || '&nbsp;'
  }</${tag}>`;
}

async function renderDatabaseValueAsync(
  {
    contentId,
    fieldId,
    entryId,
    renderContext,
    selectDatabase = selectDatabaseByContentIdOptional,
    style,
    tag = 'span',
    skipIfEmpty
  }: {
    contentId: string;
    fieldId: string;
    entryId: string;
    renderContext: RenderContext;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
    style?: string;
    tag?: string;
    skipIfEmpty?: boolean;
  },
  state: DefaultRootState
): Promise<string> {
  const database = selectDatabase(state, { contentId });
  if (!database || isBrokenDatabase(database)) {
    throw database?.error
      ? deserializeError(database.error)
      : new Error('Database is not loaded.');
  }
  const field = getDatabaseFieldRequired(database, fieldId);
  const entry = getDatabaseEntryRequired(database, entryId);
  const value = getConvertedDatabaseValue(database, field, entry, state);

  if (skipIfEmpty && isEmptyValue({ database, field, entry, value }, state)) {
    return '';
  }

  const renderedValueHtml = await renderExportViewAsync(
    { database, field, entry, value },
    state,
    renderContext,
    0,
    contentId
  );
  // Output &nbsp; if value is empty to avoid producing a self-closing div, which might confuse some export view renderers
  return `<${tag} class="${VALUE_CLASS} ${VALUE_CLASS}-${encodeHtml(value.type)}"${
    style ? ` style="${encodeHtml(style)}"` : ''
  }>${renderedValueHtml || '&nbsp;'}</${tag}>`;
}

export function renderDatabaseEntry(
  {
    contentId,
    entryId,
    renderContext,
    showName = false,
    selectDatabase = selectDatabaseByContentIdOptional
  }: {
    contentId: string;
    entryId: string;
    renderContext: RenderContext;
    showName?: boolean;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
  },
  state: DefaultRootState
): string {
  const database = selectDatabase(state, { contentId });
  if (!database || isBrokenDatabase(database)) {
    throw database?.error
      ? deserializeError(database.error)
      : new Error('Database is not loaded.');
  }
  const entry = getDatabaseEntryRequired(database, entryId);
  const values = getDatabaseFields(database).map((field) => {
    const renderedFieldNameHtml = renderFieldName({ database, field });
    const renderedValueHtml = renderDatabaseValue(
      { contentId, fieldId: field.id, entryId, renderContext, selectDatabase },
      state
    );
    return `<tr><th style="font-weight: bold">${renderedFieldNameHtml}</th><td>${renderedValueHtml}</td></tr>`;
  });

  const nameHtml = showName
    ? `<h1>${encodeHtml(getEntryName({ database, entry }, state))}</h1>`
    : '';
  return `<div class="${ENTRY_CLASS}">${nameHtml}<table><tbody>${values.join(
    ''
  )}</tbody></table></div>`;
}

function renderEntryCard(
  {
    contentId,
    entryId,
    renderContext,
    viewSettings = undefined,
    selectDatabase = selectDatabaseByContentIdOptional,
    style = '',
    outerWidth
  }: {
    contentId: string;
    entryId: string;
    renderContext: RenderContext;
    viewSettings?: DatabaseViewSettings | undefined;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
    style?: string;
    outerWidth?: number;
  },
  state: DefaultRootState
): string {
  const database = selectDatabase(state, { contentId });
  if (!database || isBrokenDatabase(database)) {
    throw database?.error
      ? deserializeError(database.error)
      : new Error('Database is not loaded.');
  }
  const shownFields = getDatabaseFields(database).filter(
    (field) => !viewSettings || shouldShowField(viewSettings.fields, field.id)
  );
  const coverField = getCoverField(database, viewSettings?.layout.coverFieldId);
  const groupField =
    viewSettings?.layout.type === DatabaseLayoutType.BOARD
      ? getGroupField(database, viewSettings)
      : undefined;
  const subgroupField =
    viewSettings?.layout.type === DatabaseLayoutType.BOARD
      ? getSubgroupField(database, viewSettings)
      : undefined;
  const cardFields = getVisibleFieldsForDatabaseCard(
    shownFields,
    coverField,
    groupField,
    subgroupField
  );
  const shownFieldIds = cardFields.map((field) => field.id);
  const { coverImage, fallback } = getEntryCover(
    { contentId, entryId, viewSettings },
    {
      dispatch: () => undefined,
      selector: (selector) => selector(state),
      selectDatabase
    }
  );

  const innerWidth =
    (outerWidth ??
      MIN_CARD_WIDTH[viewSettings?.layout.cardWidth ?? DatabaseCardWidth.MEDIUM]) - 20;

  const imageUrl =
    coverImage && 'fileId' in coverImage
      ? getImageUrl(state, contentId, coverImage.fileId)
      : undefined;

  const coverImageUrl =
    (coverImage && 'attachmentId' in coverImage && 'path' in coverImage
      ? `${selectBaseUrl(state)}${coverImage.path}`
      : coverImage && 'src' in coverImage
        ? coverImage.src
        : imageUrl
          ? imageUrl
          : undefined) ?? fallback?.src;
  const coverStyle = trimCss(`
        width: ${innerWidth}px;
        height: ${Math.round((innerWidth * 90) / 135)}px;
        overflow: hidden;
    `);
  // In static macro mode, position, transform, aspect-ratio and object-fit are all not supported. I cannot think
  // of a good way to vertically center the image.
  const coverImgStyle = trimCss(`
        width: 100%;
        aspect-ratio: 135 / 90;
        object-fit: cover;
    `);
  const coverHtml =
    coverField && coverImageUrl
      ? `<div class="${ENTRY_CLASS}-card-cover" style="${coverStyle}"><img src="${encodeHtml(
          coverImageUrl
        )}" alt="" style="${coverImgStyle}"/></div>`
      : '';

  const valuesHtml = shownFieldIds.map((fieldId, i) => {
    const field = getDatabaseFieldRequired(database, fieldId);

    let style = '';
    if (field.id === getFirstFieldId(database!)) {
      style += 'font-size: 16px; line-height: 20px; font-weight:500;';
    }
    if (i > 0 || (coverField && coverImageUrl)) {
      style += 'margin-top: 12px;';
    }
    return renderDatabaseValue(
      {
        contentId,
        fieldId,
        entryId,
        renderContext,
        selectDatabase,
        style,
        tag: 'div',
        skipIfEmpty: true
      },
      state
    );
  });

  const cardStyle = trimCss(`
        display: inline-block;
        padding: 8px;
        border-radius: 3px;
        border: 2px solid ${colors.N30};
        background: ${colors.N0};
        width: ${innerWidth}px;
        vertical-align: top;
    `);
  return `<div class="${ENTRY_CLASS}-card" style="${cardStyle}${style}">${coverHtml}${valuesHtml.join(
    ''
  )}</div>`;
}

/** Returns table layout in HTML format without styling. If there are selected field or entry
 * IDs, only those fields and entries are included. Otherwise, the entire
 * database, including field names, is included.
 * This is used when copying database content to clipboard. We only support
 * pasting to databases and not to external sources so no styling is needed.
 * */
export function renderTableLayoutWithoutStyles(
  {
    contentId,
    renderContext,
    viewSettings,
    selectDatabase = selectDatabaseByContentIdOptional,
    selectedFieldIds,
    selectedEntryIds
  }: {
    contentId: string;
    renderContext: RenderContext;
    viewSettings?: DatabaseViewSettings | undefined;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
    selectedEntryIds?: string[];
    selectedFieldIds?: string[];
  },
  state: DefaultRootState
): string {
  const database = selectDatabase(state, { contentId });
  if (!database || isBrokenDatabase(database)) {
    throw database?.error
      ? deserializeError(database.error)
      : new Error('Database is not loaded.');
  }

  const databaseView = applyDatabaseView(database, database, viewSettings, state);
  const databaseFields = getDatabaseFields(databaseView).filter((field) =>
    selectedFieldIds?.length ? selectedFieldIds.includes(field.id) : true
  );
  const databaseEntries = getDatabaseEntries(databaseView).filter((entry) =>
    selectedEntryIds?.length ? selectedEntryIds.includes(entry.id) : true
  );
  const headerCellsHtml = databaseFields.map(
    (field) =>
      `<th>${renderFieldName({
        database: databaseView,
        field
      })}</th>`
  );
  const headerHtml = `<thead><tr>${headerCellsHtml.join('')}</tr></thead>`;

  const rowsHtml = databaseEntries.map((entry) => {
    const cellsHtml = databaseFields.map((field) => {
      return `<td>${renderDatabaseValue(
        {
          contentId,
          fieldId: field.id,
          entryId: entry.id,
          renderContext,
          selectDatabase
        },
        state
      )}</td>`;
    });
    return `<tr>${cellsHtml.join('')}</tr>`;
  });
  const bodyHtml = `<tbody>${rowsHtml.join('')}</tbody>`;

  return selectedEntryIds?.length || selectedFieldIds?.length
    ? `<table>${bodyHtml}</table>`
    : `<table>${headerHtml}${bodyHtml}</table>`;
}

/**
 * Returns table layout in HTML format with styling. There is an async
 * call to fetch the media URL.
 * This is used in HTML and PDF export.
 */
async function renderTableLayoutAsync(
  {
    contentId,
    renderContext,
    viewSettings = undefined,
    selectDatabase = selectDatabaseByContentIdOptional,
    selectedFieldIds,
    selectedEntryIds
  }: {
    contentId: string;
    renderContext: RenderContext;
    viewSettings?: DatabaseViewSettings | undefined;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
    selectedEntryIds?: string[];
    selectedFieldIds?: string[];
  },
  state: DefaultRootState
): Promise<string> {
  const database = selectDatabase(state, { contentId });
  if (!database || isBrokenDatabase(database)) {
    throw database?.error
      ? deserializeError(database.error)
      : new Error('Database is not loaded.');
  }
  const databaseView = applyDatabaseView(database, database, viewSettings, state);
  const databaseFields = getDatabaseFields(databaseView).filter((field) =>
    selectedFieldIds?.length ? selectedFieldIds.includes(field.id) : true
  );
  const databaseEntries = getDatabaseEntries(databaseView).filter((entry) =>
    selectedEntryIds?.length ? selectedEntryIds.includes(entry.id) : true
  );

  const colsHtml = databaseFields.map(
    (field) =>
      `<col style="width: ${field.width ?? TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH}px"/>`
  );
  const colgroupHtml = `<colgroup>${colsHtml.join('')}</colgroup>`;

  const headerCellsHtml = databaseFields.map(
    (field) =>
      `<th style="font-weight: bold">${renderFieldName({
        database: databaseView,
        field
      })}</th>`
  );
  const headerHtml = `<thead><tr>${headerCellsHtml.join('')}</tr></thead>`;

  const rowsHtml = await Promise.all(
    databaseEntries.map(async (entry) => {
      const cellsHtml = await Promise.all(
        databaseFields.map(async (field) => {
          return `<td>${await renderDatabaseValueAsync(
            {
              contentId,
              fieldId: field.id,
              entryId: entry.id,
              renderContext,
              selectDatabase
            },
            state
          )}</td>`;
        })
      );
      return `<tr>${cellsHtml.join('')}</tr>`;
    })
  );
  const bodyHtml = `<tbody>${rowsHtml.join('')}</tbody>`;

  // These would be the styles to render the column widths like in the table view. Unfortunately, they cause the
  // table to be cut off in the PDF export, and we have no way of knowing here whether we are creating an export
  // for Scroll Viewport, the Scroll Documents Reader or for Scroll PDF Exporter.
  // const minWidth = `${databaseView.fields.reduce<number>((p, field) => p + (field.width ?? TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH), 0)}px`;
  // const width = databaseView.fields.some((field) => field.width == null) ? '100%' : `${databaseView.fields.reduce<number>((p, field) => p + field.width!, 0)}px`;
  // const tableStyle = `min-width: ${minWidth}; width: ${width}; table-layout: fixed; overflow-wrap: anywhere`;

  let tableStyle = '';
  if (databaseFields.some((field) => field.width == null)) {
    tableStyle += trimCss(`
            width: 100%;
        `);
  }
  return selectedEntryIds?.length || selectedFieldIds?.length
    ? `<table style="${tableStyle}">${bodyHtml}</table>`
    : `<table style="${tableStyle}">${colgroupHtml}${headerHtml}${bodyHtml}</table>`;
}

function renderGalleryLayout(
  {
    contentId,
    renderContext,
    viewSettings = undefined,
    selectDatabase = selectDatabaseByContentIdOptional
  }: {
    contentId: string;
    renderContext: RenderContext;
    viewSettings?: DatabaseViewSettings | undefined;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
  },
  state: DefaultRootState
): string {
  const database = selectDatabase(state, { contentId });
  if (!database || isBrokenDatabase(database)) {
    throw database?.error
      ? deserializeError(database.error)
      : new Error('Database is not loaded.');
  }
  const databaseView = applyDatabaseView(database, database, viewSettings, state);

  const cardsHtml = getDatabaseEntries(databaseView).map((entry) => {
    return renderEntryCard(
      {
        contentId,
        entryId: entry.id,
        renderContext,
        viewSettings,
        selectDatabase,
        style: 'margin: 10px;'
      },
      state
    );
  });

  return cardsHtml.join('');
}

type BoardContext = {
  contentId: string;
  renderContext: RenderContext;
  viewSettings: DatabaseViewSettings;
  selectDatabase?: typeof selectDatabaseByContentIdOptional;
};

export function renderBoardLayout(
  boardContext: BoardContext,
  state: DefaultRootState,
  intl: IntlShape
): string {
  const isNewGroupingEnabled = getExperimentValue('databasesBoardGrouping', 'enabled', {
    fireExperimentExposure: true
  });

  return isNewGroupingEnabled
    ? renderBoardLayoutNew(boardContext, state, intl)
    : renderBoardLayoutLegacy(boardContext, state);
}

function renderBoardLayoutLegacy(
  {
    contentId,
    renderContext,
    viewSettings = undefined,
    selectDatabase = selectDatabaseByContentIdOptional
  }: {
    contentId: string;
    renderContext: RenderContext;
    viewSettings?: DatabaseViewSettings | undefined;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
  },
  state: DefaultRootState
): string {
  const database = selectDatabase(state, { contentId });
  if (!database || isBrokenDatabase(database)) {
    throw database?.error
      ? deserializeError(database.error)
      : new Error('Database is not loaded.');
  }

  const groupField = getGroupField(database, viewSettings!);
  const showUndefinedColumn =
    groupField &&
    database.entries.some((entryId) => {
      const entry = getDatabaseEntryRequired(database, entryId);
      const value = getConvertedDatabaseValue(database, groupField, entry, state);
      return isEmptyValue({ database, field: groupField, entry, value }, state);
    });

  const colOuterWidth =
    MIN_CARD_WIDTH[viewSettings?.layout.cardWidth ?? DatabaseCardWidth.MEDIUM];
  const colInnerWidth = colOuterWidth - 16;

  const colStyle = trimCss(`
        display: inline-block;
        vertical-align: top;
        margin: 10px;
        background: #F5F6F8;${
          '' /* Looks like colors.N20A, because in static macro mode, rgba() is not supported */
        }
        border-radius: 3px;
        padding: 8px;
        width: ${colInnerWidth}px;
    `);

  const selectOptions = [
    ...(showUndefinedColumn ? [undefined] : []),
    ...(groupField?.settings.selectOptions ?? [])
  ];

  const colsHtml = selectOptions?.map((option: SelectOption | undefined) => {
    const selectOptionTag = getSelectOptionTag(option);
    const colHeaderHtml = `<div class="${DATABASE_CLASS}-column-header">${selectOptionTag}</div>`;
    const optionId = option?.id;
    const entryIds = database.entries.filter((entryId) => {
      const entry = getDatabaseEntryRequired(database, entryId);
      const value = getConvertedDatabaseValue(database, groupField!, entry, state);
      return (
        value.type === DatabaseFieldType.SELECT &&
        (optionId != null
          ? value.value.includes(optionId)
          : isEmptyValue({ database, field: groupField!, entry, value }, state))
      );
    });

    const cardsHtml = entryIds.map((entryId) => {
      return renderEntryCard(
        {
          contentId,
          entryId,
          renderContext,
          viewSettings,
          selectDatabase,
          style: 'margin-top: 8px;',
          outerWidth: colInnerWidth
        },
        state
      );
    });
    return `<div class="${DATABASE_CLASS}-column" style="${colStyle}">${colHeaderHtml}${cardsHtml.join(
      ''
    )}</div>`;
  });

  return colsHtml.join('');
}

function renderBoardLayoutNew(
  boardContext: BoardContext,
  state: DefaultRootState,
  intl: IntlShape
): string {
  const {
    contentId,
    viewSettings,
    selectDatabase = selectDatabaseByContentIdOptional
  } = boardContext;
  const database = selectDatabase(state, { contentId });
  if (!database || isBrokenDatabase(database)) {
    throw database?.error
      ? deserializeError(database.error)
      : new Error('Database is not loaded.');
  }

  const subgroupField = getSubgroupField(database, viewSettings);
  const groups = selectSortedGroups(state, {
    contentId,
    viewSettings,
    viewId: ''
  });
  const subgroups = selectSortedSubgroups(state, {
    contentId,
    viewSettings,
    viewId: ''
  });

  return subgroupField
    ? renderBoardLayoutSubgroups(boardContext, state, intl, groups, subgroups)
    : renderBoardLayoutColumns(boardContext, state, intl, groups);
}

function renderBoardLayoutSubgroups(
  boardContext: BoardContext,
  state: DefaultRootState,
  intl: IntlShape,
  groups: DatabaseGroup[],
  subgroups: DatabaseGroup[]
): string {
  const marginTopBottom = token('space.250', '20px');
  const subgroupsHtml = subgroups?.map((subgroup: DatabaseGroup) => {
    return `<div class="${DATABASE_CLASS}-subgroup" style="margin-top: ${marginTopBottom}; margin-bottom: ${marginTopBottom};">${renderBoardLayoutSubgroupHeader(
      intl,
      subgroup
    )}${renderBoardLayoutColumns(boardContext, state, intl, groups, subgroup)}</div>`;
  });

  return subgroupsHtml.join('');
}

function renderBoardLayoutColumns(
  boardContext: BoardContext,
  state: DefaultRootState,
  intl: IntlShape,
  groups: DatabaseGroup[],
  subgroup?: DatabaseGroup
): string {
  return `<div class="${DATABASE_CLASS}-columns">${groups
    .map((group) => renderBoardLayoutColumn(boardContext, state, intl, group, subgroup))
    .join('')}</div>`;
}

function renderBoardLayoutColumn(
  {
    contentId,
    renderContext,
    viewSettings,
    selectDatabase = selectDatabaseByContentIdOptional
  }: BoardContext,
  state: DefaultRootState,
  intl: IntlShape,
  group: DatabaseGroup,
  subgroup?: DatabaseGroup
): string {
  const entryCardMarginTop = token('space.100', '8px');
  const colHeaderHtml = renderBoardLayoutColumnHeader(intl, group);
  const colOuterWidth =
    MIN_CARD_WIDTH[viewSettings.layout.cardWidth ?? DatabaseCardWidth.MEDIUM];
  const colInnerWidth = colOuterWidth - 16;

  const entrySet = selectEntriesWithGrouping(state, {
    contentId,
    viewSettings,
    viewId: '',
    groupKey: group.key,
    subgroupKey: subgroup?.key
  });
  const entryIds = Array.from(entrySet);

  const cardsHtml = entryIds.map((entryId) => {
    return renderEntryCard(
      {
        contentId,
        entryId,
        renderContext,
        viewSettings,
        selectDatabase,
        style: `margin-top: ${entryCardMarginTop};`,
        outerWidth: colInnerWidth
      },
      state
    );
  });

  const colStyle = trimCss(`
      display: inline-block;
      vertical-align: top;
      margin: 10px;
      background: #F5F6F8;${
        '' /* Looks like colors.N20A, because in static macro mode, rgba() is not supported */
      }
      border-radius: 3px;
      padding: 8px;
      width: ${colInnerWidth}px;
  `);

  return `<div class="${DATABASE_CLASS}-column" style="${colStyle}">${colHeaderHtml}${cardsHtml.join(
    ''
  )}</div>`;
}

function getGroupTitle(intl: IntlShape, group: DatabaseGroup) {
  return group.isEmptyValueGroup
    ? intl.formatMessage(
        viewsMessages['view-settings-panel-group.sort-by-manual-no-value-group-text'],
        {
          fieldname: group.groupField.name
        }
      )
    : group.displayValue;
}

function renderBoardLayoutColumnHeader(intl: IntlShape, group: DatabaseGroup): string {
  function getHeaderDisplayValue(): string {
    if (group.type === DatabaseFieldType.SELECT && !group.isEmptyValueGroup) {
      const selectOption = (group as DatabaseGroupSelectField).selectOption;
      return getSelectOptionTag(selectOption);
    }

    return `<div style="font-weight: bold">${getGroupTitle(intl, group)}</div>`;
  }

  return `<div class="${DATABASE_CLASS}-column-header">${getHeaderDisplayValue()}</div>`;
}

function renderBoardLayoutSubgroupHeader(
  intl: IntlShape,
  subgroup: DatabaseGroup
): string {
  const marginBottom = token('space.050', '4px');
  const fontSize = token('font.heading.small', '16px');
  const marginLeft = token('space.150', '12px');

  return `<h2 style="margin-bottom: ${marginBottom}; font-size: ${fontSize}; margin-left: ${marginLeft}; font-weight: 500;">${encodeHtml(
    getGroupTitle(intl, subgroup)
  )}</h2>`;
}

export async function renderDatabaseAsync(
  {
    contentId,
    renderContext,
    viewSettings = undefined,
    showName = false,
    selectDatabase = selectDatabaseByContentIdOptional,
    databaseName
  }: {
    contentId: string;
    renderContext: RenderContext;
    viewSettings?: DatabaseViewSettings | undefined;
    showName?: boolean;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
    databaseName: string;
  },
  state: DefaultRootState,
  intl: IntlShape
): Promise<string> {
  const layout = viewSettings?.layout?.type ?? DatabaseLayoutType.TABLE;
  let renderedDatabaseHtml;
  switch (layout) {
    case DatabaseLayoutType.BOARD:
      renderedDatabaseHtml = renderBoardLayout(
        { contentId, renderContext, viewSettings: viewSettings!, selectDatabase },
        state,
        intl
      );
      break;

    case DatabaseLayoutType.GALLERY:
      renderedDatabaseHtml = renderGalleryLayout(
        { contentId, renderContext, viewSettings, selectDatabase },
        state
      );
      break;

    default:
      renderedDatabaseHtml = await renderTableLayoutAsync(
        { contentId, renderContext, viewSettings, selectDatabase },
        state
      );
      break;
  }

  const containerStyle = trimCss(`
        overflow-wrap: anywhere;${
          '' /* Unfortunately, Confluence removes this in static macro mode. */
        }
        font-size: 14px;
    `);
  const nameHtml = showName ? `<h1>${encodeHtml(databaseName)}</h1>` : '';
  return `<div class="${DATABASE_CLASS} ${DATABASE_CLASS}-layout-${encodeHtml(
    layout
  )}" style="${containerStyle}">${nameHtml}${renderedDatabaseHtml}</div>`;
}
